import { useCallback, useMemo, useRef } from 'react'
import { GoogleMap, MarkerF } from '@react-google-maps/api'
import { ICoordinates } from 'src/types'
import { mapStyles } from 'src/constants'
import MapMarkerDark from 'src/assets/files/map-marker-dark.svg'

interface IProps {
  data: ICoordinates[]
}

// Default container size for the map
const containerStyle = {
  width: '100%',
  maxWidth: '600px',
  height: '100%',
}

export const ConsumerScansLocationsMap = ({ data }: IProps) => {
  const mapRef = useRef<google.maps.Map | null>(null)

  const hasOne = data?.length === 1
  const center = useMemo(
    () => (hasOne ? { lat: data[0].latitude, lng: data[0].longitude } : undefined),
    [hasOne, data],
  )

  const onLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      mapRef.current = mapInstance

      if (!data || data.length === 0) return

      if (data.length === 1) {
        // Country-level default zoom for a single scan
        const p = { lat: data[0].latitude, lng: data[0].longitude }
        mapInstance.setCenter(p)
        mapInstance.setZoom(6) // tweak: 4-6 gives “see most of the country”
        return
      }

      const bounds = new window.google.maps.LatLngBounds()
      for (const loc of data) bounds.extend({ lat: loc.latitude, lng: loc.longitude })
      mapInstance.fitBounds(bounds, 60)
    },
    [data],
  )

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      onLoad={onLoad}
      options={{
        clickableIcons: false,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
        disableDefaultUI: false,
        zoomControl: false,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_TOP,
        },
        fullscreenControlOptions: {
          position: google.maps.ControlPosition.RIGHT_BOTTOM,
        },

        gestureHandling: 'greedy',
        keyboardShortcuts: true,
        draggable: true,
        mapTypeId: 'roadmap',
        styles: mapStyles,
        maxZoom: 13,
        minZoom: 2,
      }}
      center={hasOne ? center : undefined}
      zoom={hasOne ? 6 : undefined}
    >
      {data?.map((loc: ICoordinates, idx: number) => (
        <MarkerF
          icon={MapMarkerDark}
          key={`consumer-scan-marker-${idx}`}
          position={{ lat: loc.latitude, lng: loc.longitude }}
        />
      ))}
    </GoogleMap>
  )
}
